import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { DateRangeDropdownComponent } from '@niceltd/cxone-components/date-time-pickers';
import { DateRangeWrapperComponent } from '@niceltd/sol/date-range-picker';
import { DropdownComponent } from '@niceltd/sol/dropdown';
import { OptionComponent } from '@niceltd/sol/select';
import moment, { MomentInput } from 'moment';
import { DateRangeSelectorService } from 'src/app/reusables/date-range-selector/date-range-selector.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManagerDateRangeInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';

interface DateRangeOptionModel {
    id: number;
    showDateOnDropdown: boolean;
    title: string;
    from: () => Date;
    to: () => Date;
}

@Component({
    selector: 'cv-cxone-date-range-selector',
    templateUrl: './cxone-date-range-selector.component.html',
    styleUrls: ['./cxone-date-range-selector.component.scss']
})
export class CxoneDateRangeSelectorComponent implements OnInit, OnChanges {
    @Input() pageName?: string = null;
    @Input() earliestDate: MomentInput;
    @Input() availableDateRanges: ModuleManagerDateRangeInfo[];
    @Input() settingsManager?: SlideoutSettingsManager;
    @Input() selectedDateRange: ModuleManagerDateRangeInfo;
    @Input() dropdownContainer?;
    @Input() maxDays?: number;

    /** Minimum date allowed (how far back the user can pick a date). Default is 25 months ago */
    @Input() minDate = new Date().addMonths(-25);
    @Output() selectedDateRangeChange = new EventEmitter<ModuleManagerDateRangeInfo>();

    //#region Single Select Date Picker vars

    /** Model to bind to for single-select date picker */
    singleDate: Date;

    /** Date format to use for single select date picker */
    dateFormat = 'MMM d, yyyy';

    //#endregion



    //#region Date range selector vars (multiselect dates)

    /** this sets the selectable date range in the custom portion of the cxone date range dropdown component */
    customDateRange: {minDate: Date; maxDate: Date};

    /** Options that are in the dropdown list. This should be all options in moduleManager.availableDateRanges that are also in the allowedCXoneDateRanges below */
    dateRangeOptions: ModuleManagerDateRangeInfo[];

    /** Previously selected option. Used to keep track of option selected prior to picking 'Custom range' so if the user clicks out, it can revert back. */
    previouslySelectedDateRangeSol: ModuleManagerDateRangeInfo;

    /** SOL Date Range Dropdown (Today, Yesterday, Custom range, etc) */
    selectedDateRangeSol: ModuleManagerDateRangeInfo;

    /** SOL custom date picker model (start - end date of the calendar) */
    solDateRange: any;

    /** If the calendar for Custom date range is showing or not */
    solDatePickerShowing = false;

    /** Used to track the localized value of the custom range option */
    public customRangeText = 'Custom range';

    //#endregion



    //#region Misc/Shared vars
    
    /** Maximum date allowed (how far forward the user can pick a date). Default is today */
    maxDate = new Date();

    /** Local timezone offset from UTC */
    localOffset = new Date().getTimezoneOffset() / 60;

    /** List of supported date ranges that CXone can use. This does NOT limit values based on date ranges and should include every possible range allowed */
    private allowedCXoneDateRanges = [
        'Today',
        'Yesterday',
        'Last 7 Days',
        'Last Week',
        'Last Month',
        'Last 30 Days',
        'Last 90 Days',

        // Custom should always be last for organization purposes (that way it is always the final index in the array)
        'Custom...'
    ];

    //#endregion

    private _solDatePickerContainer: ElementRef;
    private cxoneDateRangesById: Map<number, DateRangeOptionModel> = null;
    private cxoneDateRangeIndexById: Map<number, number> = null;

    maxDaysChanged = false;

    constructor(
        private service: DateRangeSelectorService,
        private cd: ChangeDetectorRef,
        private cvLocalizationService: CVLocalizationService,
    ) {
        this.dateFormat = window['CVMediumDateFormat'] ?? 'MMM d, yyyy';
    }

    /** The dropdown element (sol-dropdown) */
    @ViewChild('drDropdown', { read: DropdownComponent })
    set drDropdown(v: DropdownComponent) {
        // eslint-disable-next-line eqeqeq
        if (v == undefined) {
            return;
        }
        this.cxoneDateRangesById = new Map();
        this.cxoneDateRangeIndexById = new Map();
        for (let i = 0; i < v.options.length; i++) {
            const option = v.options[i];
            this.cxoneDateRangesById.set(option.id, option);
            this.cxoneDateRangeIndexById.set(option.id, i + 1);
        }
    }

    /** Sol calendar (multi-select range) */
    @ViewChild('solDatePickerContainer', { read: ElementRef })
    set solDatePickerContainer(v: ElementRef) {
        this._solDatePickerContainer = v;
    }

    /** Handles click off of calendar so it can be closed without picking a new date range */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (this.solDatePickerShowing) {
            const target = event.target as HTMLElement;
            if (!this._solDatePickerContainer.nativeElement.contains(target)) {
                this.solDatePickerShowing = false;
                this.revertDateRangeSol();
            }
        }
    }

    //#region Angular lifecycle events

    async ngOnInit() {
        const localizationTask = this.cvLocalizationService.GetSection('CXone_Date_Range_Selector');

        // this.selectedDateRange.utcOffset is set in ModuleGenericSettingsUtil.cs in the GenericControlSettings() function
        this.minDate = this.minDate.addHours(this.selectedDateRange.utcOffset + this.localOffset); // this.localOffset removes browser timezone (aka UTC time)
        this.maxDate = this.maxDate.addHours(this.selectedDateRange.utcOffset + this.localOffset); // this.selectedDateRange.utcOffset adds back in dashboard timezone
        this.setSelectableDateRange();

        await localizationTask;
        this.customRangeText = this.cvLocalizationService.getCachedText('CXone_Date_Range_Selector', 'Custom', 'Custom range');
        this.updateAvailableDateRanges();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.availableDateRanges?.currentValue) {
            this.updateAvailableDateRanges();
            this.cd.detectChanges();
        }

        if (changes.selectedDateRange) {
            this.selectedDateRangeSol = changes.selectedDateRange.currentValue;
            this.updateSelectedDateRange();
            this.cd.detectChanges();
        }

        this.dynamicDateRangeOptionsCheck(); // Necessary check for the dynamic date ranges.

        if (changes.maxDays?.currentValue && changes.maxDays?.previousValue && !changes.maxDays?.firstChange) {
            this.maxDaysChanged = true;
        }
    }

    //#endregion



    //#region Date range selector functions

    /** Revert the date range to the previous value. Used when clicking outside of the calendar */
    revertDateRangeSol() {
        this.selectedDateRangeSol = this.previouslySelectedDateRangeSol;
    }

    /** defaults the selectable date range in custom range to be last 25 months */
    setSelectableDateRange() {
        const minDate = new Date(this.minDate.setHours(0,0,0,0));
        const maxDate = new Date(this.maxDate.setHours(23,59,59,999));
        this.customDateRange = {minDate, maxDate};
    }

    /** Updates the selected date range. This allows the dropdown to be labelled correcly and the singleDate value to get populated on load (from ngOnChanges) */
    updateSelectedDateRange() {
        if (this.selectedDateRangeSol) {
            // Set the single date picker if a value is in state
            this.singleDate = this.selectedDateRange?.startDate ??
                new Date().addHours(this.selectedDateRangeSol.utcOffset + this.localOffset);

            // If the selectedDate range is custom (has a start - end format)
            if (this.selectedDateRangeSol.name.includes(' - ')) {
                // Set the calendar values to match what is in state
                this.solDateRange = {
                    start: new Date(this.selectedDateRangeSol.startDate),
                    end: new Date(this.selectedDateRangeSol.endDate),
                };

                // Set the dropdown option to custom...
                this.selectedDateRangeSol = this.dateRangeOptions[this.dateRangeOptions.length - 1];
            }
        }
    }

    /** Saves the selected date range */
    async saveSelectedDateRangeSol() {
        if (this.selectedDateRange) {
            await this.service.saveDateRange(this.pageName, this.selectedDateRange.name);

            // Fire events to moduleManager and also on this object that the date range has changed
            if (this.settingsManager != null) {
                this.settingsManager.selectedDateRange = this.selectedDateRange;
                this.settingsManager.onSelectedDateRangeChanged.next(this.selectedDateRange);
            } 
            this.selectedDateRangeChange.emit(this.selectedDateRange);
        }
    }

    formatDate(d: MomentInput) {
        return moment(d).format(CVMomentDateFormat) !== "Invalid date" ? moment(d).format(CVMomentDateFormat) : "";
    }

    /** Updates the available date ranges. These will have all date constraint allowed ranges and intersects against 'allowedCXoneDateRanges' for valid dates. */
    updateAvailableDateRanges() {
        this.dateRangeOptions = this.availableDateRanges.filter(
            dateRange => this.allowedCXoneDateRanges.includes(dateRange.name)
        );
        
        // Every option needs to use the custom template, so add that property to each value on the object now
        this.dateRangeOptions.forEach((dateRange) => {
            dateRange['customTmpl'] = true;
        });

        // Update the 'Custom...' text to be 'Custom range' (or whatever CXone wants it to be)
        this.dateRangeOptions[this.dateRangeOptions.length - 1].displayName =
            this.cvLocalizationService.getCachedText('CXone_Date_Range_Selector', 'Custom', 'Custom range');
    }

    /** Assigns the selectedDateRange and calls the save function */
    setAndSaveDateRange(newDateRange: ModuleManagerDateRangeInfo) {
        this.selectedDateRange = newDateRange;
        this.selectedDateRangeSol = newDateRange;
        this.saveSelectedDateRangeSol();
    }

    /** Makes sure that the selectedDateRangeSol exists in the dateRangeOptions array, and if not selects the first object. Also it makes sure that the selected date range if not larger than the maxDays */
    dynamicDateRangeOptionsCheck() {
        if (this.maxDaysChanged && this.selectedDateRangeSol) {
            if (!this.dateRangeOptions?.find((dateRange) => dateRange.displayName === this.selectedDateRangeSol?.displayName)) {
                // It will enter here only if the Reporting Set was changed and the previosly selected date range option is not available anymore.
                this.selectedDateRangeSol = this.dateRangeOptions[0];
                if (this.settingsManager) { // This is needed to simulate a save but only in the settings DATE dropdown and not in the quick date picker.
                    this.setAndSaveDateRange(this.selectedDateRangeSol); // Saving the selected date range temporarily in case it changed on the previous lines.
                }
                this.cd.detectChanges();
            } else if (this.selectedDateRangeSol.displayName != this.selectedDateRange.displayName && this.selectedDateRangeSol.displayName !== "Custom range") {
                // This else if is needed for cases when the Reporting Set and the selected date range option changed and the user cancels that change so the last selected option is reverted.
                // Custom range will always be in all Reporting Sets that's why is excluded and also this option is set with a different name (the date range is set as the name)
                this.selectedDateRangeSol = this.selectedDateRange;
                this.cd.detectChanges();
            }

            if (this.selectedDateRange.name.includes(' - ')) { // Needed to check if the date range is not larger than the maxDays.
                const startDate = this.selectedDateRange.startDate;
                const endDate = this.selectedDateRange.endDate.addDays(1).addSeconds(-1);
                const differenceInTime = endDate.getTime() - startDate.getTime(); // Calculating the time difference of the dates.
                const differenceInDays = Math.round(differenceInTime / (1000 * 3600 * 24)); // Calculating the number of days between the dates.
                if (differenceInDays > this.maxDays) {
                    endDate.addDays(-differenceInDays); // Changing the start date to fit the max date range.
                    endDate.addDays(Number(this.maxDays));
                    const dateRangeObj = {
                        name: `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`,
                        displayName: this.cvLocalizationService.getCachedText('CXone_Date_Range_Selector', 'Custom', 'Custom range'),
                        utcOffset: this.selectedDateRange.utcOffset + this.localOffset
                    };
                    this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(dateRangeObj);
                    this.selectedDateRangeSol = this.dateRangeOptions[this.dateRangeOptions.length - 1];
                    if (this.settingsManager) {
                        this.saveSelectedDateRangeSol(); // Saving the selected date range temporarily in case it changed on the previous lines.
                    }
                    this.cd.detectChanges();
                }
            }
            this.maxDaysChanged = false;
        }
    }

    //#endregion



    //#region Event handlers

    /** Fired when the single (maxDays === 1) calendar date changes */
    async onSingleDateChanged(e: Date) {
        // Sets the date as a custom single date
        this.singleDate = e;
        const dateRangeObj = {
            name: `${this.formatDate(e.getTime())} - ${this.formatDate(e.getTime())}`,
            displayName: "Custom...",
            utcOffset: this.selectedDateRange.utcOffset + this.localOffset
        };
        this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(dateRangeObj);

        await this.setAndSaveDateRange(this.selectedDateRange);
    }

    /** Fired when the date range selector (multi-day selector) starts closing */
    onSolDatePickerClosed($event: string|void) {
        this.solDatePickerShowing = false;
    }

    /** Fired when the date range selector (multi-day selector) starts opening. This converts the calendar dates to match what is in selectedDateRangeSol (dropdown) */
    onSolDatePickerOpened() {
        this.solDateRange = {
            start: new Date(this.selectedDateRangeSol.startDate),
            end: new Date(this.selectedDateRangeSol.endDate),
        };
    }

    /** Fired when a date is picked on the sol date range (multi-day selector). This first both on start and end, so it checks for both before continuing. */
    onSolDateSelected($event: DateRange<Date>) {
        if ($event.start && $event.end) {
            const differenceInTime = $event.end.getTime() - $event.start.getTime(); // Calculating the time difference of the dates.
            const differenceInDays = Math.round(differenceInTime / (1000 * 3600 * 24)); // Calculating the number of days between the dates.
            if (differenceInDays > this.maxDays) {
                $event.end.addDays(-differenceInDays); // Changing the start date to fit the max date range.
                $event.end.addDays(Number(this.maxDays));
            }

            const dateRangeObj = {
                name: `${this.formatDate($event.start)} - ${this.formatDate($event.end)}`,
                displayName: this.cvLocalizationService.getCachedText('CXone_Date_Range_Selector', 'Custom', 'Custom range'),
                utcOffset: this.selectedDateRange.utcOffset + this.localOffset
            };
            this.selectedDateRange = ModuleManagerDateRangeInfo.fromObject(dateRangeObj);
            this.saveSelectedDateRangeSol();
        }
    }

    /** Fired when a new item in the Date dropdown is clicked (such as Today, Yesterday, Last 7 Days, Custom range, etc) */
    onSolOptionClick(pickedDateRange: OptionComponent) {
        if (pickedDateRange.id === 'Custom...') {
            // Open the calendar (date picker wrapper). Save happens after valid date range picked
            this.previouslySelectedDateRangeSol = this.selectedDateRangeSol;
            setTimeout(() => { this.solDatePickerShowing = true; }, 0); // Timeout is needed. Otherwise the @HostListener will close the calendar immediately
        }
        else {
            // All other options are predefined, save happens immediately
            const match = this.dateRangeOptions.find((dateRange) => dateRange.name === pickedDateRange.id);
            this.setAndSaveDateRange(match);
        }
    }

    //#endregion
}
